import { useFormContext, FieldValues, Path, PathValue } from 'react-hook-form'
import classNames from 'classnames'
import styles from '../CheckboxGroup/styles.module.scss'
import labelStyles from '../../shared/fieldLabel.module.scss'
import { FieldErrorMessage } from '../../shared/ErrorMessage'

type SingleCheckboxProps<T extends FieldValues> = {
  id: Path<T>
  label?: string
  checkboxLabel: string
  required?: boolean
  size?: 'small' | 'medium'
  layout?: 'row' | 'column'
  disabled?: boolean
}

export function SingleCheckbox<T extends FieldValues>({
  id,
  label,
  checkboxLabel,
  required = false,
  size = 'medium',
  layout = 'column',
  disabled = false
}: SingleCheckboxProps<T>) {
  const {
    register,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext<T>()

  const checked = !!watch(id)

  const { onChange: rhfOnChange, ...reg } = register(id, { required })

  const labelClasses = classNames({
    [labelStyles.disabled]: disabled,
    [labelStyles.focused]: checked,
    [labelStyles.required]: required
  })

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    rhfOnChange(e)
    setValue(id, e.target.checked as PathValue<T, Path<T>>, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    })
  }

  return (
    <fieldset
      className={classNames(styles.CheckboxGroup, styles[size], styles[layout])}
    >
      {label && (
        <legend className={labelStyles.FieldLabel}>
          <span className={`${labelStyles.FieldLabel__name} ${labelClasses}`}>
            {label}
          </span>
        </legend>
      )}

      <label
        className={classNames(styles.checkboxOption, {
          [styles.disabled]: disabled,
          [styles.selected]: checked
        })}
      >
        <input
          type="checkbox"
          {...reg}
          onChange={handleOnChange}
          disabled={disabled}
          className={styles.checkboxInput}
        />
        <span className={styles.fakeCheckbox} aria-hidden="true" />
        <span className={styles.checkboxLabel}>{checkboxLabel}</span>
      </label>

      {errors[id] && !disabled && (
        <FieldErrorMessage name={id} errors={errors} />
      )}
    </fieldset>
  )
}
